import React, { FC } from 'react';
import { reduxForm, Field } from 'redux-form';
import { newThemeInput } from '_fields/inputs';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Text } from 'shared-components';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { parseCurrency } from '_fields/parsers';
import { ApplyWizardSteps, STEP_BUSINESS_FURTHER_DETAILS } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import {
  LoanWizardShell,
  LoanSubmitButton,
  getLoanPhaseIndex,
  getLoanFaqForRoute,
} from '@components/Apply/WizardShell';

type FurtherBusinessDetailsProps = {
  handleSubmit: () => void;
  invalid: boolean;
  years?: number;
  saving?: boolean;
};

const FurtherBusinessDetailsWrapper2: FC<FurtherBusinessDetailsProps> = ({
  handleSubmit,
  invalid,
  saving: savingOnSubmit,
}) => {
  const form = useForm();
  const { saving, currentPath } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);
  const faq = getLoanFaqForRoute(currentPath);
  const isSubmitting = !!(saving || savingOnSubmit);

  return (
    <LoanWizardShell currentStepIndex={phase} faq={faq}>
      <Helmet>
        <title>Further Business Details</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Tax & Revenue Information
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Tax & Revenue Information
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  FEDERAL TAX ID*
                </span>
                <Field
                  data-testid="fed-tax-id"
                  name="business_ein"
                  component={newThemeInput}
                  type="tel"
                  label="Tax ID"
                  placeholder="Federal Tax ID* (XX-XXXXXXX)"
                  formatAsFederalTaxId
                  autoFocus
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  ANNUAL GROSS REVENUE*
                </span>
                <Field
                  name="business_revenue"
                  component={newThemeInput}
                  labelText="Annual Gross Revenue*"
                  parse={parseCurrency}
                  formatAsNumber
                  labelClassName="infoFormCustomLabel"
                  data-testid="business_revenue"
                  className="!bg-dawn3"
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton invalid={invalid} saving={isSubmitting} data-testid="info_continue">
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

const FurtherBusinessDetailsWrapper1 = reduxForm<FurtherBusinessDetailsProps, FurtherBusinessDetailsProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_FURTHER_DETAILS].zodSchema),
})(FurtherBusinessDetailsWrapper2);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    years: appSelector(state, 'business_years'),
  };
};

export const FurtherBusinessDetails = withOnSubmit(
  connect(mapStateToProps)(FurtherBusinessDetailsWrapper1),
  STEP_BUSINESS_FURTHER_DETAILS
);
